// TUT-588a — o cliente HTTP da configuração do Financeiro gerencial
// (Configurações → Financeiro): categorias, centros de custo, orçamento e ajustes.
import { demoApi } from "../demo-data";
import type {
  AjustesFinanceiros,
  CategoriaGerencial,
  CentroDeCusto,
  ConfiguracaoFinanceira,
  CriarCategoriaInput,
  CriarCentroInput,
  LinhaDeOrcamento,
  MudarAjustesInput,
  MudarCategoriaInput,
  MudarCentroInput,
  PainelFinanceiro,
  DreGerencial,
  PedidoDoDre,
  PedidoDaRentabilidade,
  PedidoDoPrevistoRealizado,
  PrevistoRealizado,
  Rentabilidade,
} from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

const BASE = "/financeiro-gerencial";
const corpo = (valor: unknown) => ({ method: "POST", body: JSON.stringify(valor) });
const em = (metodo: "PATCH" | "PUT", valor: unknown) => ({ method: metodo, body: JSON.stringify(valor) });

export const apiDoFinanceiroGerencial = {
  /** TUT-588b — o Painel financeiro de um mês (`AAAA-MM`); sem ele, o mês de hoje. */
  painelFinanceiro(mes?: string): Promise<PainelFinanceiro> {
    if (useDemoApi) return demoApi.painelFinanceiro(mes);
    return request<PainelFinanceiro>(`${BASE}/painel${mes ? `?mes=${encodeURIComponent(mes)}` : ""}`);
  },
  /** TUT-588c — o DRE gerencial de um mês, contra o mês anterior ou o mesmo mês do ano anterior, de um centro ou de todos. */
  dreGerencial(pedido: PedidoDoDre = {}): Promise<DreGerencial> {
    if (useDemoApi) return demoApi.dreGerencial(pedido);
    const parametros = new URLSearchParams();
    if (pedido.mes) parametros.set("mes", pedido.mes);
    if (pedido.comparar) parametros.set("comparar", pedido.comparar);
    if (pedido.centro) parametros.set("centro", pedido.centro);
    const consulta = parametros.toString();
    return request<DreGerencial>(`${BASE}/dre${consulta ? `?${consulta}` : ""}`);
  },
  /** TUT-588c2 — a visão previsto × realizado de um mês e de um centro (ou de todos). */
  previstoRealizado(pedido: PedidoDoPrevistoRealizado = {}): Promise<PrevistoRealizado> {
    if (useDemoApi) return demoApi.previstoRealizado(pedido);
    const parametros = new URLSearchParams();
    if (pedido.mes) parametros.set("mes", pedido.mes);
    if (pedido.centro) parametros.set("centro", pedido.centro);
    const consulta = parametros.toString();
    return request<PrevistoRealizado>(`${BASE}/previsto-realizado${consulta ? `?${consulta}` : ""}`);
  },
  /** TUT-588d — a Rentabilidade por cliente: o mês (o último do período) e quantos meses olhar. */
  rentabilidade(pedido: PedidoDaRentabilidade = {}): Promise<Rentabilidade> {
    if (useDemoApi) return demoApi.rentabilidade(pedido);
    const parametros = new URLSearchParams();
    if (pedido.mes) parametros.set("mes", pedido.mes);
    if (pedido.periodo) parametros.set("periodo", pedido.periodo);
    const consulta = parametros.toString();
    return request<Rentabilidade>(`${BASE}/rentabilidade${consulta ? `?${consulta}` : ""}`);
  },
  configuracaoFinanceira(ano?: number): Promise<ConfiguracaoFinanceira> {
    if (useDemoApi) return demoApi.configuracaoFinanceira(ano);
    return request<ConfiguracaoFinanceira>(`${BASE}/configuracao${ano ? `?ano=${ano}` : ""}`);
  },
  criarCategoriaGerencial(input: CriarCategoriaInput): Promise<CategoriaGerencial> {
    if (useDemoApi) return demoApi.criarCategoriaGerencial(input);
    return request<CategoriaGerencial>(`${BASE}/categorias`, corpo(input));
  },
  mudarCategoriaGerencial(id: string, input: MudarCategoriaInput): Promise<CategoriaGerencial> {
    if (useDemoApi) return demoApi.mudarCategoriaGerencial(id, input);
    return request<CategoriaGerencial>(`${BASE}/categorias/${encodeURIComponent(id)}`, em("PATCH", input));
  },
  criarCentroDeCusto(input: CriarCentroInput): Promise<CentroDeCusto> {
    if (useDemoApi) return demoApi.criarCentroDeCusto(input);
    return request<CentroDeCusto>(`${BASE}/centros`, corpo(input));
  },
  mudarCentroDeCusto(id: string, input: MudarCentroInput): Promise<CentroDeCusto> {
    if (useDemoApi) return demoApi.mudarCentroDeCusto(id, input);
    return request<CentroDeCusto>(`${BASE}/centros/${encodeURIComponent(id)}`, em("PATCH", input));
  },
  gravarOrcamento(items: LinhaDeOrcamento[]): Promise<{ items: LinhaDeOrcamento[] }> {
    if (useDemoApi) return demoApi.gravarOrcamento(items);
    return request<{ items: LinhaDeOrcamento[] }>(`${BASE}/orcamento`, em("PUT", { items }));
  },
  mudarAjustesFinanceiros(input: MudarAjustesInput): Promise<AjustesFinanceiros> {
    if (useDemoApi) return demoApi.mudarAjustesFinanceiros(input);
    return request<AjustesFinanceiros>(`${BASE}/ajustes`, em("PATCH", input));
  },
  concluirGuiaFinanceiro(): Promise<AjustesFinanceiros> {
    if (useDemoApi) return demoApi.concluirGuiaFinanceiro();
    return request<AjustesFinanceiros>(`${BASE}/guia/concluir`, corpo({}));
  },
};
